import React, { useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import KendoActionsCell from '@components/Kendo/ActionsCell';
import { Dialog } from '@progress/kendo-react-dialogs';
import I18n, { translate } from '@utils/i18n';
import { editProposalPath, proposalPath } from 'routes';
import { Button } from '@ui';
import SendProposal from '@features/Proposals/SendProposal/SendProposalModalView';
import ColumnMenuContext from './ColumnMenuContext';
import redirectPath from '../helpers/redirectPath';
import StatusModal from './StatusModal';

const namespace = 'features.proposals.index.table.actions';

function ActionsCell({ dataItem, colSpan, className, ariaColumnIndex, style }) {
  const { screen, screenId } = useContext(ColumnMenuContext);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [sendProposalModalVisible, setSendProposalModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);

  const authToken = useMemo(() => document.querySelector('meta[name="csrf-token"]').getAttribute('content'), []);

  const toggleDeleteDialog = useCallback(() => setDeleteDialogVisible((prev) => !prev), []);
  const toggleSendProposalModal = useCallback(() => setSendProposalModalVisible((prev) => !prev), []);
  // Explicit open/close rather than a toggle. StatusModal calls onClose after an await,
  // so if the dialog was already dismissed while the save was in flight a toggle would
  // reopen it on success.
  const openStatusModal = useCallback(() => setStatusModalVisible(true), []);
  const closeStatusModal = useCallback(() => setStatusModalVisible(false), []);

  const actionsList = useMemo(() => {
    const list = [];

    list.push({
      title: translate('view', { namespace }),
      faClass: 'fa-eye',
      link: dataItem.pdfPath,
      blank: true
    });

    if (dataItem.downloadPath) {
      list.push({
        title: translate('download', { namespace }),
        faClass: 'fa-download',
        link: dataItem.downloadPath,
        blank: true
      });
    }

    list.push({
      title: translate('send', { namespace }),
      faClass: 'fa-envelope',
      onClick: toggleSendProposalModal
    });
    if (dataItem.canEdit) {
      list.push({
        title: translate('edit', { namespace }),
        faClass: 'fa-pen-to-square',
        link: editProposalPath({ id: dataItem.id, _options: true })
      });

      // IP-15353: same gate as Edit on purpose. canEdit is ProposalPolicy#edit?,
      // which is what the status endpoint authorizes against, so the menu cannot
      // offer a change the server will refuse.
      list.push({
        title: translate('change_status', { namespace }),
        faClass: 'fa-arrow-right-arrow-left',
        onClick: openStatusModal
      });
    }

    list.push({
      title: translate('delete', { namespace }),
      faClass: 'fa-trash-can',
      onClick: toggleDeleteDialog
    });

    return list;
  }, [dataItem.id, dataItem.canEdit, toggleDeleteDialog, toggleSendProposalModal, openStatusModal]);

  const deletePath = useMemo(
    () => proposalPath({ id: dataItem.id, redirect_path: redirectPath({ screen, screenId }), _options: true }),
    [dataItem.id, screen, screenId]
  );

  return (
    <>
      <KendoActionsCell
        colSpan={colSpan}
        className={className}
        ariaColumnIndex={ariaColumnIndex}
        style={style}
        actionsList={actionsList}
      />

      {deleteDialogVisible && (
        <Dialog title={I18n.t('generic.confirm')} className="qmb-dialog" onClose={toggleDeleteDialog}>
          <p>{translate('confirm_delete', { namespace })}</p>
          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <form action={deletePath} method="POST">
              <input type="hidden" value="delete" name="_method" />
              <input name="authenticity_token" type="hidden" value={authToken} />
              <Button onClick={toggleDeleteDialog}>{I18n.t('generic.cancel')}</Button>
              <Button type="submit" variant="primary">
                {I18n.t('generic.ok')}
              </Button>
            </form>
          </div>
        </Dialog>
      )}

      {sendProposalModalVisible && <SendProposal proposal={dataItem} onClose={toggleSendProposalModal} />}

      {statusModalVisible && <StatusModal dataItem={dataItem} onClose={closeStatusModal} />}
    </>
  );
}

ActionsCell.propTypes = {
  ariaColumnIndex: PropTypes.number.isRequired,
  style: PropTypes.object,
  className: PropTypes.string,
  colSpan: PropTypes.number.isRequired,
  dataItem: PropTypes.shape({
    id: PropTypes.number.isRequired,
    pdfPath: PropTypes.string.isRequired,
    downloadPath: PropTypes.string,
    canEdit: PropTypes.bool,
    statusId: PropTypes.number,
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired
};

ActionsCell.defaultProps = {
  style: null,
  className: ''
};

export default ActionsCell;
